import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommunityVideo } from '../../../api/community/types';
import { getImageUrl } from '../../../utils/imageUrl';
import CommunityActionMenu from './CommunityActionMenu';
import './ActionCard.css';

interface ActionCardProps {
  video: CommunityVideo;
  onEdit?: (video: CommunityVideo) => void;
  onPublish?: (video: CommunityVideo) => void;
  onUnpublish?: (video: CommunityVideo) => void;
  onDelete?: (video: CommunityVideo) => void;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  video,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu
}) => {
  const navigate = useNavigate();
  const thumbnailUrl = video.thumbnailUrl || video.contentUrl;
  const imageUrl = getImageUrl(thumbnailUrl);
  const hasErrorRef = useRef(false);

  // Reset error ref when image URL changes
  useEffect(() => {
    hasErrorRef.current = false;
  }, [imageUrl]);

  // Get tags from API response - display first 2 tags if available
  const tags = video.tags || [];
  const displayTags = tags.slice(0, 2); // Show first 2 tags

  const getCategoryPath = (category: string): string => {
    if (category === 'action') return 'action';
    if (category === 'level_up') return 'level_up';
    if (category === 'social') return 'social';
    return 'action'; // Default
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the menu
    if ((e.target as HTMLElement).closest('.action-card-menu')) {
      return;
    }
    const categoryPath = getCategoryPath(video.category);
    navigate(`/community/${categoryPath}/${video._id}`);
  };

  return (
    <div className="action-card" onClick={handleCardClick}>
      <div className="action-card-image-container">
        <img
          src={hasErrorRef.current ? '/placeholder-video.png' : (imageUrl || '/placeholder-video.png')}
          alt={video.title}
          className="action-card-image"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            // Only set placeholder if we haven't already tried it
            if (target.src !== window.location.origin + '/placeholder-video.png' && !hasErrorRef.current) {
              hasErrorRef.current = true;
              target.src = '/placeholder-video.png';
            } else {
              // If placeholder also fails, hide the image to prevent infinite loop
              target.style.display = 'none';
            }
          }}
        />
        {/* Dark gradient overlay at bottom with title and labels */}
        <div className="action-card-overlay">
          <h4 className="action-card-title">{video.title}</h4>
          <div className="action-card-labels-container">
            <div className="action-card-labels">
              {displayTags.map((tag, index) => (
                <span key={index} className="action-label">{tag}</span>
              ))}
            </div>
            <div className="action-card-menu" onClick={(e) => e.stopPropagation()}>
              <CommunityActionMenu
                video={video}
                onEdit={onEdit}
                onPublish={onPublish}
                onUnpublish={onUnpublish}
                onDelete={onDelete}
                isOpen={isMenuOpen}
                onToggle={onToggleMenu}
                onClose={onCloseMenu}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionCard;

